import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DashboardMetrics,
  EarningsTrendData,
  NetworkPerformance,
  CurrencyBreakdown,
  RecentFTD,
  SyncStatusItem,
  Currency,
  TimeRange,
  ViewGranularity,
} from '../types/dashboard.types';

/**
 * API Response wrapper type
 */
interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch (error) {
    console.error('Failed to get auth token:', error);
  }
  return null;
};

/**
 * Create headers with authentication
 * NOTE: Call this function at fetch time, not at module load time
 */
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Handle API response and errors
 */
const handleAPIResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData: any = await response.json().catch(() => ({
      error: 'Request failed',
      message: response.statusText,
    }));
    throw new Error((errorData as any)?.error || (errorData as any)?.message || 'API request failed');
  }

  const data: APIResponse<T> = await response.json();
  
  if (!data.success) {
    throw new Error(data.message || data.error || 'API request unsuccessful');
  }

  if (!data.data) {
    throw new Error('No data returned from API');
  }

  return data.data;
};

/**
 * Fetch dashboard metrics from API
 * Supports both daily and monthly granularity
 */
const fetchDashboardMetrics = async (
  timeRange: TimeRange,
  baseCurrency: Currency,
  selectedMonth?: string,
  selectedDate?: string,
  granularity?: ViewGranularity
): Promise<DashboardMetrics> => {
  const params = new URLSearchParams({
    timeRange,
    currency: baseCurrency,
  });
  
  // For daily granularity, pass the specific date
  if (granularity === 'daily' && selectedDate) {
    params.set('startDate', selectedDate);
    params.set('endDate', selectedDate);
    params.set('timeRange', 'custom');
  } else if (timeRange === 'month' && selectedMonth) {
    params.set('month', selectedMonth);
  }

  const response = await fetch(`/api/statistics/metrics?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleAPIResponse<DashboardMetrics>(response);
};

/**
 * Fetch earnings trend data from API
 */
const fetchEarningsTrend = async (
  timeRange: TimeRange,
  currency: Currency,
  selectedMonth?: string
): Promise<EarningsTrendData[]> => {
  const params = new URLSearchParams({
    timeRange,
    currency,
  });
  
  if (timeRange === 'month' && selectedMonth) {
    params.set('month', selectedMonth);
  }

  const response = await fetch(`/api/statistics/trend?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await handleAPIResponse<EarningsTrendData[]>(response);
  return data || [];
};

/**
 * Fetch network performance data from API
 */
const fetchNetworkPerformance = async (
  timeRange: TimeRange
): Promise<NetworkPerformance[]> => {
  const params = new URLSearchParams({
    timeRange,
  });

  const response = await fetch(`/api/statistics/network-performance?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await handleAPIResponse<NetworkPerformance[]>(response);
  return data || [];
};

/**
 * Fetch currency breakdown from API
 */
const fetchCurrencyBreakdown = async (
  timeRange: TimeRange
): Promise<CurrencyBreakdown[]> => {
  const params = new URLSearchParams({
    timeRange,
  });

  const response = await fetch(`/api/statistics/currency-breakdown?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await handleAPIResponse<CurrencyBreakdown[]>(response);
  return data || [];
};

/**
 * Fetch recent FTDs from API
 */
const fetchRecentFTDs = async (limit: number = 10): Promise<RecentFTD[]> => {
  const params = new URLSearchParams({
    limit: limit.toString(),
  });

  const response = await fetch(`/api/statistics/recent-ftds?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await handleAPIResponse<RecentFTD[]>(response);
  return data || [];
};

/**
 * Fetch sync status from networks API
 */
const fetchSyncStatus = async (): Promise<SyncStatusItem[]> => {
  const response = await fetch('/api/networks', {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const networks = await handleAPIResponse<any[]>(response);
  
  if (!networks || networks.length === 0) {
    return [];
  }

  // Transform network data to sync status format
  return networks.map((network) => ({
    networkId: network.id || network.network_id,
    networkName: network.name || network.network_name,
    lastSyncTime: network.last_sync_time || network.lastSyncTime || new Date().toISOString(),
    status: network.sync_status || network.status || 'success',
    recordsProcessed: network.records_processed || network.recordsProcessed,
    errorMessage: network.error_message || network.errorMessage,
  }));
};

/**
 * Trigger manual sync for a network
 */
const triggerSync = async (networkId: string): Promise<void> => {
  const response = await fetch(`/api/networks/${networkId}/sync`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  await handleAPIResponse<void>(response);
};

/**
 * React Query hook for dashboard metrics
 * Supports both daily and monthly granularity
 */
export const useDashboardMetrics = (
  timeRange: TimeRange, 
  baseCurrency: Currency, 
  selectedMonth?: string,
  selectedDate?: string,
  granularity?: ViewGranularity
) => {
  return useQuery({
    queryKey: ['dashboardMetrics', timeRange, baseCurrency, selectedMonth, selectedDate, granularity],
    queryFn: () => fetchDashboardMetrics(timeRange, baseCurrency, selectedMonth, selectedDate, granularity),
    refetchInterval: 30000, // 30 seconds
    staleTime: 20000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * React Query hook for earnings trend
 */
export const useEarningsTrend = (timeRange: TimeRange, currency: Currency, selectedMonth?: string) => {
  return useQuery({
    queryKey: ['earningsTrend', timeRange, currency, selectedMonth],
    queryFn: () => fetchEarningsTrend(timeRange, currency, selectedMonth),
    refetchInterval: 30000,
    staleTime: 20000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * React Query hook for network performance
 */
export const useNetworkPerformance = (timeRange: TimeRange) => {
  return useQuery({
    queryKey: ['networkPerformance', timeRange],
    queryFn: () => fetchNetworkPerformance(timeRange),
    refetchInterval: 30000,
    staleTime: 20000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * React Query hook for currency breakdown
 */
export const useCurrencyBreakdown = (timeRange: TimeRange) => {
  return useQuery({
    queryKey: ['currencyBreakdown', timeRange],
    queryFn: () => fetchCurrencyBreakdown(timeRange),
    refetchInterval: 30000,
    staleTime: 20000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * React Query hook for recent FTDs
 */
export const useRecentFTDs = (limit: number = 10) => {
  return useQuery({
    queryKey: ['recentFTDs', limit],
    queryFn: () => fetchRecentFTDs(limit),
    refetchInterval: 30000,
    staleTime: 20000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * React Query hook for sync status
 */
export const useSyncStatus = () => {
  return useQuery({
    queryKey: ['syncStatus'],
    queryFn: fetchSyncStatus,
    refetchInterval: 10000, // More frequent for sync status
    staleTime: 5000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * React Query mutation hook for triggering sync
 */
export const useTriggerSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerSync,
    onSuccess: () => {
      // Invalidate sync status to refetch
      queryClient.invalidateQueries({ queryKey: ['syncStatus'] });
      // Also invalidate metrics and network performance as they might change
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['networkPerformance'] });
      queryClient.invalidateQueries({ queryKey: ['accountPerformance'] });
    },
    retry: 1,
  });
};

/**
 * Account Performance data type
 */
export interface AccountPerformanceItem {
  accountId: string;
  accountName: string;
  networkId: string;
  platformName: string;
  ngr: number;
  deposits: number;
  withdrawals: number;
  ftds: number;
  signups: number;
  activePlayers: number;
  clicks: number;
  impressions: number;
  conversionRate: number;
  // Commission breakdown
  totalCommission: number;
  cpaCommission: number;
  revenueShareCommission: number;
  subAffiliateCommission: number;
  lastSyncAt: string | null;
  syncStatus: string;
  tags: string[];
}

interface AccountPerformanceResponse {
  data: AccountPerformanceItem[];
  summary: {
    totalNgr: number;
    totalDeposits: number;
    totalFtds: number;
    totalSignups: number;
    totalPlayers: number;
    accountCount: number;
    // Commission breakdown totals
    totalCommission: number;
    totalCpaCommission: number;
    totalRevenueShareCommission: number;
    totalSubAffiliateCommission: number;
  };
}

/**
 * Fetch account performance data from API
 * v3 - Properly passes granularity and date for daily vs monthly differentiation
 */
const fetchAccountPerformance = async (
  timeRange: TimeRange,
  selectedMonth?: string,
  granularity?: ViewGranularity,
  selectedDate?: string
): Promise<AccountPerformanceResponse> => {
  const params = new URLSearchParams({
    timeRange,
  });
  
  if (timeRange === 'month' && selectedMonth) {
    params.set('month', selectedMonth);
  }
  
  // Pass granularity so the API knows which table to query
  if (granularity) {
    params.set('granularity', granularity);
  }
  
  // For daily view, pass the specific date
  if (granularity === 'daily' && selectedDate) {
    params.set('date', selectedDate);
  }

  const response = await fetch(`/api/statistics/account-performance?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    console.error('[fetchAccountPerformance] Response not OK:', response.status);
    return { data: [], summary: { totalNgr: 0, totalDeposits: 0, totalFtds: 0, totalSignups: 0, totalPlayers: 0, accountCount: 0, totalCommission: 0, totalCpaCommission: 0, totalRevenueShareCommission: 0, totalSubAffiliateCommission: 0 } };
  }

  const json = await response.json() as { success?: boolean; data?: AccountPerformanceResponse };
  console.log('[fetchAccountPerformance] Raw JSON:', JSON.stringify(json).substring(0, 500));
  
  // Handle the nested structure: { success: true, data: { data: [...], summary: {...} } }
  if (json.success && json.data) {
    console.log('[fetchAccountPerformance] Found data.data length:', json.data.data?.length);
    console.log('[fetchAccountPerformance] First account:', JSON.stringify(json.data.data?.[0] || 'none').substring(0, 200));
    return json.data;
  }
  
  // Fallback
  console.warn('[fetchAccountPerformance] Unexpected response - success:', json.success, 'hasData:', !!json.data);
  return { 
    data: [], 
    summary: { 
      totalNgr: 0, 
      totalDeposits: 0, 
      totalFtds: 0, 
      totalSignups: 0, 
      totalPlayers: 0, 
      accountCount: 0,
      totalCommission: 0,
      totalCpaCommission: 0,
      totalRevenueShareCommission: 0,
      totalSubAffiliateCommission: 0
    } 
  };
};

/**
 * React Query hook for account performance
 * v2 - Includes granularity and date in query key for proper daily/monthly switching
 */
export const useAccountPerformance = (
  timeRange: TimeRange,
  selectedMonth?: string,
  granularity?: ViewGranularity,
  selectedDate?: string
) => {
  return useQuery({
    queryKey: ['accountPerformance', timeRange, selectedMonth, granularity, selectedDate],
    queryFn: () => fetchAccountPerformance(timeRange, selectedMonth, granularity, selectedDate),
    refetchInterval: 30000,
    staleTime: 20000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
